import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

import { GenericObject } from "../types";

import { stringifyUrl } from "./url";

declare namespace RequestUtils {
  export interface GetOptions<T, Q extends object> {
    axiosConfig?: AxiosRequestConfig<T>;
    additionalQuery?: Partial<Q>;
  }
}

export class RequestUtils {
  readonly axios: AxiosInstance;
  constructor(axios: AxiosInstance) {
    this.axios = axios;
  }

  Get<T, Q extends object = GenericObject>(url: string, options?: RequestUtils.GetOptions<T, Q>) {
    return async (query?: Partial<Q>) => {
      const { data } = await this.axios.get<T>(
        stringifyUrl(url, { ...options?.additionalQuery, ...query }),
        options?.axiosConfig,
      );
      return data;
    };
  }

  /**
   * Get with dynamic url
   */
  DynamicGet<Args extends any[], T = any, Q extends object = GenericObject>(
    url: (...args: Args) => string,
    options?: RequestUtils.GetOptions<T, Q>,
  ) {
    return async (...args: Args) => {
      const { data } = await this.axios.get<T>(
        stringifyUrl(url(...args), { ...options?.additionalQuery }),
        options?.axiosConfig,
      );
      return data;
    };
  }

  Post<Payload = any, Return = Payload>(url: string) {
    return async (payload: Payload, axiosConfig?: AxiosRequestConfig) => {
      const { data } = await this.axios.post<Payload, AxiosResponse<Return>>(url, payload, axiosConfig);
      return data;
    };
  }

  /**
   * Post with dynamic url
   */
  DynamicPost<Args extends any[], Payload = any, Return = Payload>(url: (...args: Args) => string) {
    return async (...args: [...Args, Payload]) => {
      const payload = args.pop();
      const { data } = await this.axios.post<Payload, AxiosResponse<Return>>(url(...(args as any as Args)), payload);
      return data;
    };
  }

  Patch<Payload = any, Return = Payload>(url: string) {
    return async (payload: Payload, axiosConfig?: AxiosRequestConfig) => {
      const { data } = await this.axios.patch<Payload, AxiosResponse<Return>>(url, payload, axiosConfig);
      return data;
    };
  }

  DynamicPatch<Args extends any[], Payload = any, Return = Payload>(url: (...args: Args) => string) {
    return async (...args: [...Args, Payload]) => {
      const payload = args.pop();
      const { data } = await this.axios.patch<Payload, AxiosResponse<Return>>(url(...(args as any as Args)), payload);
      return data;
    };
  }
}
